import { useState, type FormEvent, type ReactNode } from 'react';
import { apiFetch } from './api.ts';
import { useAuth } from './AuthContext.tsx';
import { LARGO_MINIMO_CLAVE, MedidorClave } from './MedidorClave.tsx';

/** Página "Mi cuenta": cambio de contraseña del propio usuario (todos los roles). */
export function CambiarClavePage(): ReactNode {
  const { usuario } = useAuth();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetir, setRepetir] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(null);
    if (nueva !== repetir) { setError('Las contraseñas nuevas no coinciden'); return; }
    if (nueva.length < LARGO_MINIMO_CLAVE) { setError(`Mínimo ${LARGO_MINIMO_CLAVE} caracteres`); return; }
    setEnviando(true);
    try {
      const r = await apiFetch<{ ok: true; otras_sesiones_cerradas: number }>('/api/auth/password', {
        method: 'PUT',
        body: JSON.stringify({ actual, nueva }),
      });
      setActual(''); setNueva(''); setRepetir('');
      setOk(
        r.otras_sesiones_cerradas > 0
          ? `Contraseña actualizada. Se cerraron tus otras ${r.otras_sesiones_cerradas === 1 ? 'sesión' : 'sesiones'} (${r.otras_sesiones_cerradas}).`
          : 'Contraseña actualizada.',
      );
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : 'Error al cambiar la contraseña');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <div>
        <h2 className="text-xl font-bold text-neutro-800">Mi cuenta</h2>
        <p className="text-sm text-neutro-500">{usuario?.email}</p>
      </div>

      <form onSubmit={enviar} className="space-y-4 rounded-2xl bg-white p-5 shadow-sm border border-contorno-var">
        <h3 className="font-semibold text-neutro-800">Cambiar contraseña</h3>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Contraseña actual</span>
          <input className="campo" type="password" required autoComplete="current-password" value={actual} onChange={(e) => setActual(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Contraseña nueva (mín. {LARGO_MINIMO_CLAVE})</span>
          <input className="campo" type="password" required minLength={LARGO_MINIMO_CLAVE} autoComplete="new-password" value={nueva} onChange={(e) => setNueva(e.target.value)} />
          <MedidorClave password={nueva} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Repetir contraseña nueva</span>
          <input className="campo" type="password" required minLength={LARGO_MINIMO_CLAVE} autoComplete="new-password" value={repetir} onChange={(e) => setRepetir(e.target.value)} />
        </label>
        <p className="text-xs text-neutro-400">
          No hace falta que tenga mayúsculas ni símbolos: una frase larga que solo vos recuerdes es más segura que "Clave1!". Se rechazan las contraseñas comunes y las que aparecieron en filtraciones.
        </p>

        {error && <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-estado-error">{error}</p>}
        {ok && <p className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-verde-400">{ok}</p>}

        <button type="submit" disabled={enviando} className="boton w-full bg-primario-400 text-white hover:bg-primario-500">
          {enviando ? 'Guardando…' : 'Cambiar contraseña'}
        </button>
      </form>
    </div>
  );
}
